import React, { useRef, useState } from 'react'
import PropertyPrice from './PropertyPrice'
import { GuestData, updateOrder } from '../../service/cart'
import Voucher from './Voucher'
import GuestSelect from './GuestSelect'
import PropertyIntro from './PropertyIntro'
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js'
import Loader from '../Loader'
import { dispatchEvent } from '../../util/dispatch-event'
import dayjs from 'dayjs';

export interface Props {
  data: any
  setData: any
  apiResponse: any
  fetchData: any
  setError: any
  setLoading: any
  error: string
  resetVoucherValue: boolean
  id: string
  intentId: string
  guests: GuestData
  setGuests: (data: GuestData) => void
  bookingData: any
  setBookingData: (data: any) => void
  originalAmount: number
  extraData: any
  setExtraData: any
}

const OrderForm: React.FunctionComponent<Props> = (props) => {
  const { extraData, setExtraData, setError, resetVoucherValue, setLoading, data, setData, error, id, intentId, apiResponse, fetchData, guests, setGuests, bookingData, setBookingData, originalAmount } = props
  const [ localLoading, setLocalLoading ] = useState<boolean>(false)

  const discounted = originalAmount - apiResponse.cart.price.total_final

  const stripe = useStripe()
  const elements = useElements()
  const formRef = useRef()

  const onGuestChange = (evt: React.ChangeEvent<HTMLSelectElement>, key: keyof GuestData) => {
    const tmp = { ...guests }
    tmp[key] = parseInt(evt.target.value)

    setGuests(tmp)
  }

  const onInputChange = (key: string, value: any): void => {
    const tmp = { ...bookingData }
    tmp[key] = value

    setBookingData(tmp)
  }

  const property = apiResponse.cart.properties[0]

  const completeBooking = async (evt: React.FormEvent) => {
    evt.preventDefault()

    if (!stripe || !elements)
      return

    setLocalLoading(true)
    dispatchEvent(document, 'gloversure.payment.form.submit')
    const formData = new FormData(evt.target as HTMLFormElement)
    const updateOrderResponse: any = await updateOrder(id, intentId, [
      ...formData,
      [
        'extras',
        btoa(JSON.stringify(extraData)),
      ],
      [
        'coupon',
        data.voucher,
      ],
    ])

    if (!updateOrderResponse.success) {
      setError('Something went wrong, please try again.');
      setLocalLoading(false)
      return;
    }

    const url = (window as any).siteInfo.url
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${url}/wp-json/accommodation/order-complete`
      },
      redirect: 'always',
    })

    setLocalLoading(false)

    if (result.error) {
      dispatchEvent(document, 'gloversure.payment.form.failed', {
        message: result.error.message,
      })
      setError(result.error.message)
      return
    }
  }

  const countries = apiResponse.content.countries
  countries.sort((a: any, b: any) => {
    if (a.name === 'United Kingdom')
          return -1

      if (b.name === 'United Kingdom')
          return 1

      return 0
  })

  const arrival = dayjs(data.properties[0].arrival.date, 'YYYY-MM-DD')
  const departure = dayjs(data.properties[0].departure.date, 'YYYY-MM-DD');
  const nights = Math.abs(arrival.diff(departure, 'days'))
  const moneyFormat = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: apiResponse.cart.currency,
  })
  let dueNow = apiResponse.cart.price.deposit || apiResponse.cart.price.balance

  return <div>
    {localLoading && <Loader />}
    <div className="checkout_guest">
      <div className="checkout_guest__booking box_shadow">
        <h3 className="box_shadow__title">Booking summary</h3>
        <PropertyIntro property={property} />

        <h3 className="box_shadow__title">
          <GuestSelect
            onGuestChange={onGuestChange}
            guests={guests}
            property={property}
          />
        </h3>

        <PropertyPrice
          extraData={extraData}
          setExtraData={setExtraData}
          data={data}
          setData={setData}
          setLoading={setLoading}
          fetchData={fetchData}
          property={property}
          price={apiResponse.cart.price}
          currency={apiResponse.cart.currency}
          includeSecurityDeposit={true}
        />
      </div>
      <div className="checkout_guest__content">
        <div className="box_shadow">
          <h3 className="box_shadow__title">Voucher</h3>
            {!!discounted && discounted > 0 &&
              <div className="checkout_property__success">
                {moneyFormat.format(discounted)} removed from price
              </div>
            }
          <Voucher onVoucherChange={(voucherValue) => {
            const tmp = { ...data }
            tmp.voucher = voucherValue

            setLoading(true)
            setData(tmp)
            fetchData(false)
          }} resetValue={resetVoucherValue} />
        </div>
        <form onSubmit={completeBooking} ref={formRef}>
          <input type="hidden" name="cottage_id" value={property.cottage_id} />
          <input type="hidden" name="full" value={apiResponse.cart.price.deposit === 0 ? 'yes' : 'no'} />
          <input type="hidden" name="fullprice" value={apiResponse.cart.price.total_final} />
          <input type="hidden" name="adults" value={guests.adults} />
          <input type="hidden" name="children" value={guests.children} />
          <input type="hidden" name="infants" value={guests.infants} />
          <input type="hidden" name="start" value={arrival.format('YYYY-MM-DD')} />
          <input type="hidden" name="nights" value={nights} />
          <div className="checkout_guest__contact box_shadow">
            <h3 className="box_shadow__title">Contact details</h3>
            <div className="checkout_guest__contact_form">
              <div>
                <select required name="title" value={bookingData['title'] || ''} onChange={(evt) => onInputChange('title', evt.target.value)}>
                  <option value="">Title *</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                  <option value="Doctor">Dr</option>
                  <option value="Other">Other</option>
                </select>
                <input required name="firstname" placeholder="First Name *" value={bookingData['firstname']} onChange={(evt) => onInputChange('firstname', evt.target.value)} />
                <input required name="lastname" placeholder="Last Name *" value={bookingData['lastname']} onChange={(evt) => onInputChange('lastname', evt.target.value)} />
              </div>
              <div>
                <input required name="address1" placeholder="Address 1 *" value={bookingData['address1']} onChange={(evt) => onInputChange('address1', evt.target.value)} />
              </div>
              <div>
                <input required name="city" placeholder="City / Town *" value={bookingData['city']} onChange={(evt) => onInputChange('city', evt.target.value)} />
                <input required name="postcode" placeholder="Post Code *" value={bookingData['postcode']} onChange={(evt) => onInputChange('postcode', evt.target.value)} />
              </div>
              <div>
                <input name="county" placeholder="County / Region / State" value={bookingData['county']} onChange={(evt) => onInputChange('county', evt.target.value)} />
                <select required name="country" value={bookingData['country']} onChange={(evt) => onInputChange('country', evt.target.value)}>
                  <option value="">Country *</option>
                  {countries.map((option: any) => {
                    return <option key={option.iso}>{option.name}</option>
                  })}
                </select>
              </div>
              <div>
                <input required name="email" placeholder="Email Address *" value={bookingData['email']} onChange={(evt) => onInputChange('email', evt.target.value)} />
                <input required name="phone" placeholder="Telephone *" value={bookingData['phone']} onChange={(evt) => onInputChange('phone', evt.target.value)} />
              </div>
            </div>
          </div>
          {apiResponse.content.custom_fields &&
            <div className="checkout_guest__other box_shadow">
              <h3 className="box_shadow__title">Other information</h3>
              {apiResponse.content.custom_fields.map((field: any) => {
                const options = field.options.map((option: any, index: number) => {
                  return <option key={index}>{option}</option>
                })
                return <div key={field.label}>
                  {field.label}
                  <select>
                    <option>Select</option>
                    {options}
                  </select>
                  <p className="text--sm" dangerouslySetInnerHTML={{ __html: field.notes }} />
                </div>
              })}
            </div>
          }
          <div className="checkout_guest__pay_method box_shadow">
            {error &&
              <div className="checkout_property__error">
                {error}
              </div>
            }
            <h3 className="box_shadow__title">Payment method</h3>
            <PaymentElement />
          </div>
          <div className="checkout_guest__t_c box_shadow">
            <h3 className="box_shadow__title">Terms &amp; Conditions</h3>
            <span>
              <input
                onChange={(evt) => {
                  if (!evt.target.checked)
                    return

                  dispatchEvent(document, 'gloversure.payment.form.allow_personal_data', {
                    form: formRef.current,
                  })
                }}
                required
                type="checkbox"
              />
              I agree to the
              <a href={apiResponse.content.url.terms_and_conditions} target="_blank">Terms &amp; Conditions</a>
              and
              <a href={apiResponse.content.url.privacy_policy} target="_blank">Privacy and Cookie Policy</a>
            </span>
          </div>
          <input type="submit" className="checkout_guest__finish" value="Complete Booking" />
        </form>
      </div>
    </div>
  </div>
}

export default OrderForm 

